/**
 * US State → IANA timezone mapping (#1656).
 *
 * Used by ClubSettingsGeneralPage to auto-default the club's timezone when
 * an admin picks a state. Multi-tz states default to the more populous side
 * (admin can override via the explicit dropdown).
 *
 * Defaults are documented in #1656 — TL;DR: pick where most population
 * lives. Users can always override via the COMMON_TIMEZONES dropdown.
 */

import type { USState } from './usStates';

export const STATE_TO_TIMEZONE: Record<USState, string> = {
  // Eastern Time
  CT: 'America/New_York',
  DE: 'America/New_York',
  FL: 'America/New_York', // most population (Miami/Tampa/Orlando); panhandle is CT
  GA: 'America/New_York',
  IN: 'America/New_York', // most counties + Indianapolis; far-west is CT
  KY: 'America/New_York', // Louisville/Lexington; western half is CT
  MA: 'America/New_York',
  MD: 'America/New_York',
  ME: 'America/New_York',
  MI: 'America/New_York', // most state; UP western edge is CT
  NC: 'America/New_York',
  NH: 'America/New_York',
  NJ: 'America/New_York',
  NY: 'America/New_York',
  OH: 'America/New_York',
  PA: 'America/New_York',
  RI: 'America/New_York',
  SC: 'America/New_York',
  VA: 'America/New_York',
  VT: 'America/New_York',
  WV: 'America/New_York',

  // Central Time
  AL: 'America/Chicago',
  AR: 'America/Chicago',
  IA: 'America/Chicago',
  IL: 'America/Chicago',
  KS: 'America/Chicago', // most state; small western strip is MT
  LA: 'America/Chicago',
  MN: 'America/Chicago',
  MO: 'America/Chicago',
  MS: 'America/Chicago',
  ND: 'America/Chicago', // most state; far-west is MT
  NE: 'America/Chicago', // most state; panhandle is MT
  OK: 'America/Chicago',
  SD: 'America/Chicago', // most population; western is MT
  TN: 'America/Chicago', // Nashville/Memphis; eastern third is ET
  TX: 'America/Chicago', // El Paso is MT but tiny
  WI: 'America/Chicago',

  // Mountain Time
  CO: 'America/Denver',
  ID: 'America/Denver', // Boise/most state; panhandle is PT
  MT: 'America/Denver',
  NM: 'America/Denver',
  UT: 'America/Denver',
  WY: 'America/Denver',
  // AZ uses MT but no DST — special case
  AZ: 'America/Phoenix',

  // Pacific Time
  CA: 'America/Los_Angeles',
  NV: 'America/Los_Angeles',
  OR: 'America/Los_Angeles', // most population; small eastern strip is MT
  WA: 'America/Los_Angeles',

  // Alaska / Hawaii
  AK: 'America/Anchorage',
  HI: 'Pacific/Honolulu',
};

/**
 * Curated timezone dropdown options for the Settings UI. Covers all common
 * US zones (with the AZ-no-DST caveat) plus a small set of international
 * zones for virtual clubs. Admin picks from this list when the state-based
 * default is wrong (e.g. Indiana club whose members actually meet in the
 * CT-side counties).
 */
export const COMMON_TIMEZONES: { value: string; label: string }[] = [
  // US
  { value: 'America/New_York', label: 'Eastern Time — New York' },
  { value: 'America/Chicago', label: 'Central Time — Chicago' },
  { value: 'America/Denver', label: 'Mountain Time — Denver' },
  { value: 'America/Phoenix', label: 'Mountain Time (no DST) — Phoenix' },
  { value: 'America/Los_Angeles', label: 'Pacific Time — Los Angeles' },
  { value: 'America/Anchorage', label: 'Alaska Time — Anchorage' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time — Honolulu' },
  // International (rare today, but virtual clubs may need)
  { value: 'America/Toronto', label: 'Eastern Time — Toronto' },
  { value: 'America/Vancouver', label: 'Pacific Time — Vancouver' },
  { value: 'Europe/London', label: 'UK / London' },
  { value: 'Europe/Berlin', label: 'Central Europe — Berlin' },
  { value: 'Asia/Tokyo', label: 'Japan — Tokyo' },
  { value: 'Australia/Sydney', label: 'Australia — Sydney' },
  { value: 'UTC', label: 'UTC' },
];

/**
 * Render a human-readable label for a stored IANA timezone, falling back to
 * the raw IANA value if the tz isn't in COMMON_TIMEZONES (e.g. a legacy
 * value or an admin who PATCHed an unusual zone via the API).
 */
export function timezoneLabel(tz: string | null | undefined): string {
  if (!tz) return 'Not set';
  const match = COMMON_TIMEZONES.find((t) => t.value === tz);
  return match ? match.label : tz;
}

/**
 * Compute the current short abbreviation for a tz (e.g. "EDT", "PST").
 * Caller is responsible for handling DST flips by recomputing on render.
 */
export function timezoneAbbreviation(tz: string | null | undefined): string {
  if (!tz) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}
